import { Observable } from 'rxjs';
import { repeat, retryWhen, takeUntil, share, publishReplay, refCount, delay } from 'rxjs/operators';

/**
 * Identical to shareReplay() but weak.
 * shareReplay() will not disconnect when all subscribers disconnect (to keep cache), this operator will.
 * See https://github.com/ReactiveX/rxjs/pull/2910
 */
export const weakShareReplay =
  <T>(bufferSize?: number, windowTime?: number) =>
  (source: Observable<T>) =>
    source.pipe<T, T>(publishReplay<T>(bufferSize, windowTime), refCount<T>());

/**
 * Returns a new Observable that repeats the source Observable indefinitely while applying a delay between each repeat cycle.
 * Error's are ignored and will not break the cycle.
 *
 * The returned Observable does not multicast (share) the input Observable (unless the input is already shared). This means
 * that each subscription will start a new poller.
 * @param interval The delay duration in milliseconds to wait before repeating the source observable.
 */
export const poll =
  (interval = 5000) =>
  (source: Observable<any>) =>
    source.pipe(
      delay(interval),
      repeat(),
      retryWhen((errors) => errors.pipe(delay(interval)))
    );

/**
 * Returns a new Observable that repeats the source Observable indefinitely while applying a delay with a short-circuit between each repeat cycle.
 * Error's are ignored and will not break the cycle.
 *
 * Each emission from `reset` will short circuiting the delay, causing it to fast forward to the end, effectively invoking an immidiate repeat.
 *
 * If a short-circuit notification arrives while the source observable is running (i.e. not while waiting between cycles) it will be cancelled
 * and a new invokcation of it will follow immediately.
 * For example, if the source observable represents HTTP request and a short-circuit notification arrived while the request is in-flight it
 * will be cancelled.
 *
 * The returned Observable does not multicast (share) the input Observable (unless the input is already shared). This means
 * that each subscription will start a new poller.
 *
 * > `pollReset` is identical to `poll` without a short-circut mechanizem.
 * @see {@link poll}
 * @param reset An observable of notifications to short-circuit the current delay.
 * @param interval The delay duration in milliseconds to wait before repeating the source observable.
 */
export const pollReset = (reset: Observable<any>, interval = 5000) => {
  return (source: Observable<any>) => source.pipe(poll(interval), takeUntil(reset), repeat());
};

/**
 * Returns a new Observable that multicasts (shares) and repeats the source Observable indefinitely while applying a delay between each repeat cycle.
 * Error's are ignored and will not break the cycle.
 *
 * Because the Observable is multicasting it makes the stream `hot` (shared), all of the subscribers will use the same polling cycle.
 * The first subscriber will start the cycle, invoking a subscription at the source Observable, all subsequent subscribers will not.
 *
 * When all subscribers have unsubscribed it will unsubscribe from the source Observable, effectively stopping the repeat cycle.
 *
 * Having a shared Observable give' allows Caching. To enable caching set the `bufferSize` to a value greater then 0.
 * When the buffer size is greater then 0 new subscribers will immidiatly get an emission of values based on the buffer value and avilable cache stored.
 * When the buffer size equals 0 new subscribers will only get notified about upcoming emissions.
 *
 * > `pollShare` is the hot version of `poll`
 * @see {@link poll}
 *
 * @param bufferSize An observable of notifications to short-circuit the current delay.
 * @param interval The delay duration in milliseconds to wait before repeating the source observable.
 */
export const pollShare =
  (bufferSize: number, interval = 5000) =>
  (source: Observable<any>) =>
    source.pipe(poll(interval), bufferSize < 1 ? share() : weakShareReplay(bufferSize));

/**
 * Returns a new Observable that multicasts (shares) and repeats the source Observable indefinitely while applying a delay with a short-circuit between each repeat cycle.
 * Error's are ignored and will not break the cycle.
 *
 * Because the Observable is multicasting it makes the stream `hot` (shared), all of the subscribers will use the same polling cycle.
 * The first subscriber will start the cycle, invoking a subscription at the source Observable, all subsequent subscribers will not.
 *
 * When all subscribers have unsubscribed it will unsubscribe from the source Observable, effectively stopping the repeat cycle.
 *
 * Having a shared Observable give' allows Caching. To enable caching set the `bufferSize` to a value greater then 0.
 * When the buffer size is greater then 0 new subscribers will immidiatly get an emission of values based on the buffer value and avilable cache stored.
 * When the buffer size equals 0 new subscribers will only get notified about upcoming emissions.
 *
 * Each emission from `reset` will short circuiting the delay, causing it to fast forward to the end, effectively invoking an immidiate repeat.
 *
 * If a short-circuit notification arrives while the source observable is running (i.e. not while waiting between cycles) it will be cancelled
 * and a new invokcation of it will follow immediately.
 * For example, if the source observable represents HTTP request and a short-circuit notification arrived while the request is in-flight it
 * will be cancelled.
 *
 * > This is a combination of {@link pollReset} and {@link pollShare}
 *
 * @see {@link poll}
 * @see {@link pollReset}
 * @see {@link pollShare}
 *
 * @param reset An observable of notifications to short-circuit the current delay.
 * @param bufferSize An observable of notifications to short-circuit the current delay.
 * @param interval The delay duration in milliseconds to wait before repeating the source observable.
 */
export const pollResetShare =
  (reset: Observable<any>, bufferSize: number, interval = 5000) =>
  (source: Observable<any>) =>
    source.pipe(pollReset(reset, interval), bufferSize < 1 ? share() : weakShareReplay(bufferSize));
